import type { FreigabeAblage } from './ablage/bauordnungAblage';
import type { FreigabeFesthalten } from '../../services/unterlagenAblage';
import type { Bauordnung } from './types';

/**
 * Der Lauf nach einer Freigabe — Freigabe-Journal und Ablage — als reine
 * Zustandslogik, ohne React. Der Hook `useFreigabeLauf` haelt den Zustand im
 * Modul; der Reiter „Ausgabe & Freigabe" liest ihn nur.
 *
 * ═══ WARUM NICHT IM REITER ═══════════════════════════════════════════════
 * Journal und Ablage dauern bis zu einer Minute. Wechselt man in der Zeit den
 * Reiter, wird er ausgehaengt — lag die Sperre in SEINEM Zustand, war sie beim
 * Zurueckkommen weg (Freigeben, „Neue Fassung eroeffnen", „Erneut ablegen"
 * klickbar, waehrend der Lauf weiterging), und das Ergebnis des Laufs
 * (Hinweis, Auftrag fuer „Erneut festhalten") ging verloren. Das Modul bleibt
 * eingehaengt; also liegt beides dort.
 *
 * ═══ JE PROJEKT EIN EINTRAG ═════════════════════════════════════════════
 * Der Zustand ist nach Projekt-Id geschluesselt: ein Lauf, der nach einem
 * Projektwechsel zu Ende geht, schreibt in SEIN Projekt und nie in das, das
 * gerade offen ist. Die Sperre gilt ebenso nur fuer das Projekt, in dem der
 * Lauf laeuft.
 *
 * ═══ ERGEBNIS GEHOERT ZUR FREIGABE ══════════════════════════════════════
 * Hinweis und Ablage-Ergebnis tragen den Schluessel ihrer Freigabe
 * (Kopfsatz-Id + `freigegeben_am`). Gezeigt werden sie nur, solange der
 * Kopfsatz GENAU diese Freigabe traegt — ein Ergebnis von Rev. 1 erscheint
 * weder am Entwurf danach noch an Rev. 2.
 */

/** `freigabe`: der Kopfsatz wird freigegeben · `journal`: Planungsstand festhalten · `ablage`: PDFs ablegen. */
export type LaufPhase = 'freigabe' | 'journal' | 'ablage';

/** Wem ein Ergebnis gehoert: dem Kopfsatz und SEINEM Freigabezeitpunkt. */
export interface FreigabeSchluessel {
    ordnungId: string;
    freigegebenAm: string;
}

/**
 * Der Planungsstand der Freigabe konnte nicht festgehalten werden (Stufe 6.1).
 * `wiederholung`: der Auftrag fuer „Erneut festhalten" mit GENAU dem Anker des
 * ersten Versuchs — `null`, wenn keiner entstand (dann nur der Hinweis).
 */
export interface JournalHinweis {
    fuer: FreigabeSchluessel;
    hinweis: string;
    wiederholung: FreigabeFesthalten | null;
}

export interface AblageStand {
    fuer: FreigabeSchluessel;
    ergebnis: FreigabeAblage;
}

/** Der Stand EINES Projekts. `phase: null` heisst: es laeuft nichts. */
export interface FreigabeLauf {
    phase: LaufPhase | null;
    journal: JournalHinweis | null;
    ablage: AblageStand | null;
}

/** Alle Projekte, nach Projekt-Id. */
export type FreigabeLaeufe = Readonly<Record<string, FreigabeLauf>>;

export const KEIN_LAUF: FreigabeLauf = { phase: null, journal: null, ablage: null };
export const KEINE_LAEUFE: FreigabeLaeufe = {};

/** Was der Reiter sieht — schon auf das offene Projekt und die aktuelle Freigabe zugeschnitten. */
export interface FreigabeLaufAnsicht {
    /** `true` von der Freigabe bis Journal UND Ablage durch sind — Freigabe, Revision, Ablage, Festhalten gesperrt. */
    laeuft: boolean;
    phase: LaufPhase | null;
    journal: JournalHinweis | null;
    ablage: FreigabeAblage | null;
}

/** Sichtbarer Grund an jedem Knopf, den ein laufender Lauf sperrt. */
export const LAUF_SPERRGRUND = 'Die Freigabe wird noch festgehalten und abgelegt. Bitte einen Moment warten.';

/** Der Schluessel der Freigabe, die der Kopfsatz gerade traegt — `null` fuer einen Entwurf. */
export const freigabeSchluessel = (
    o: Pick<Bauordnung, 'id' | 'freigegeben_am'> | null | undefined,
): FreigabeSchluessel | null =>
    (o && o.id && o.freigegeben_am ? { ordnungId: o.id, freigegebenAm: o.freigegeben_am } : null);

/**
 * Dieselbe Freigabe? Der Zeitpunkt wird als Augenblick verglichen, nicht als
 * Zeichenkette — die Rueckgabe des Updates und das neu geladene Buendel sollen
 * nicht an einer Schreibweise auseinanderfallen.
 */
export const gleicheFreigabe = (a: FreigabeSchluessel | null, b: FreigabeSchluessel | null): boolean => {
    if (!a || !b || a.ordnungId !== b.ordnungId) return false;
    if (a.freigegebenAm === b.freigegebenAm) return true;
    const ta = Date.parse(a.freigegebenAm);
    return Number.isFinite(ta) && ta === Date.parse(b.freigegebenAm);
};

export const laufVon = (l: FreigabeLaeufe, projectId: string): FreigabeLauf => l[projectId] ?? KEIN_LAUF;

const mit = (l: FreigabeLaeufe, projectId: string, f: (lauf: FreigabeLauf) => FreigabeLauf): FreigabeLaeufe =>
    ({ ...l, [projectId]: f(laufVon(l, projectId)) });

/**
 * Beginnt einen Lauf. `null`, wenn in diesem Projekt schon einer laeuft — der
 * Aufrufer bricht dann ab (ein Doppelklick startet nichts zweites).
 * Eine neue FREIGABE verwirft die Ergebnisse der vorigen; „Erneut ablegen"
 * und „Erneut festhalten" lassen das jeweils andere Ergebnis stehen.
 */
export const laufStarten = (l: FreigabeLaeufe, projectId: string, phase: LaufPhase): FreigabeLaeufe | null => {
    if (!projectId || laufVon(l, projectId).phase) return null;
    return mit(l, projectId, lauf => (phase === 'freigabe'
        ? { phase, journal: null, ablage: null }
        : { ...lauf, phase }));
};

/** Naechster Schritt desselben Laufs — nur, solange er laeuft. */
export const laufWeiter = (l: FreigabeLaeufe, projectId: string, phase: LaufPhase): FreigabeLaeufe =>
    (laufVon(l, projectId).phase ? mit(l, projectId, lauf => ({ ...lauf, phase })) : l);

export const laufBeenden = (l: FreigabeLaeufe, projectId: string): FreigabeLaeufe =>
    (laufVon(l, projectId).phase ? mit(l, projectId, lauf => ({ ...lauf, phase: null })) : l);

/**
 * Ergebnis des Freigabe-Journals. `fehler: null` heisst: festgehalten — ein
 * stehender Hinweis faellt weg. Der Hinweis steht an der Freigabe, bis die
 * naechste ihn ersetzt.
 */
export const journalVermerken = (
    l: FreigabeLaeufe,
    projectId: string,
    fuer: FreigabeSchluessel,
    fehler: { hinweis: string; wiederholung: FreigabeFesthalten | null } | null,
): FreigabeLaeufe =>
    mit(l, projectId, lauf => ({ ...lauf, journal: fehler ? { fuer, ...fehler } : null }));

/** Ergebnis der Ablage. `null` kommt nur aus `schreiben` selbst (der Hook zeigt den Fehler schon). */
export const ablageVermerken = (
    l: FreigabeLaeufe,
    projectId: string,
    fuer: FreigabeSchluessel,
    ergebnis: FreigabeAblage | null,
): FreigabeLaeufe =>
    mit(l, projectId, lauf => ({ ...lauf, ablage: ergebnis ? { fuer, ergebnis } : null }));

/**
 * Die Ansicht fuer das offene Projekt: die Sperre gilt fuer das Projekt, in
 * dem der Lauf laeuft; Ergebnisse nur, wenn der Kopfsatz zu diesem Projekt
 * gehoert und GENAU die Freigabe traegt, zu der sie entstanden.
 */
export const laufAnsicht = (
    l: FreigabeLaeufe,
    projectId: string,
    ordnung: Pick<Bauordnung, 'id' | 'project_id' | 'freigegeben_am'> | null | undefined,
): FreigabeLaufAnsicht => {
    const lauf = laufVon(l, projectId);
    const jetzt = ordnung && ordnung.project_id === projectId ? freigabeSchluessel(ordnung) : null;
    return {
        laeuft: lauf.phase !== null,
        phase: lauf.phase,
        journal: lauf.journal && gleicheFreigabe(lauf.journal.fuer, jetzt) ? lauf.journal : null,
        ablage: lauf.ablage && gleicheFreigabe(lauf.ablage.fuer, jetzt) ? lauf.ablage.ergebnis : null,
    };
};
